import {DOCUMENT, isPlatformBrowser} from '@angular/common';
import {HttpClient} from '@angular/common/http';
import {DestroyRef, Injectable, PLATFORM_ID, inject, signal} from '@angular/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {NavigationEnd, Router} from '@angular/router';
import {Capacitor} from '@capacitor/core';
import {configureMobileSystemBars, opaqueStatusBarColor} from '../shared/mobile-chrome/mobile-status-bar';
import {distinctUntilChanged, filter, firstValueFrom, map, skip} from 'rxjs';
import {AuthService} from '../auth/auth.service';
import {ApiResponse, AuthSession} from '../auth/auth.models';
import {isAuthSessionActive} from '../auth/auth.storage';
import {buildApiUrl} from '../shared/api/api-url';
import {CatalogCacheService} from '../shared/catalogs/catalog-cache.service';
import {CatalogStorageService} from '../shared/catalogs/catalog-storage.service';
import {isServerRenderedRoutePathname, stripLocalizedRouteLanguageFromPathname} from '../shared/i18n/app-language';
import {APPEARANCE_SCHEMA, AppearancePalette, AppearancePaletteDto, SaveAppearancePaletteDto,
  catalogPalettes, normalizeColors, paletteStyles, standardPalette, validCatalog, validPalette} from './appearance-palette';

const LAST_PALETTE_KEY = 'invemory.appearance.last';
const LAST_CHOICE_KEY = 'invemory.appearance.lastChoice';
const CHOICE_PREFIX = 'invemory.appearance.choice.';
const SELECTED_PREFIX = 'invemory.appearance.selected.';
export const PUBLIC_APPEARANCE_KEY = 'invemory.appearance.public.v1';
const STANDARDS_KEY = 'invemory.appearance.standards.v1';

@Injectable({providedIn: 'root'})
export class AppearanceService {
  private readonly auth = inject(AuthService);
  private readonly http = inject(HttpClient);
  private readonly cache = inject(CatalogCacheService);
  private readonly storage = inject(CatalogStorageService);
  private readonly router = inject(Router);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly browser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly url = buildApiUrl('/v1/appearance/palettes');
  private owner = '';
  private generation = 0;
  private accountGeneration = 0;
  private readonly deleted = new Set<string>();
  private readonly deleting = new Set<string>();
  private started = false;
  private routerReady = false;
  private draft: AppearancePalette | null = null;
  private nativeChromeUpdate = Promise.resolve();
  private nativeBackground = '#edf2f8';
  private nativeNavigationBackground = '#edf2f8';
  private nativeWebViewBackground = '#edf2f8';
  private appliedKeys: string[] = [];
  private system?: MediaQueryList;
  readonly palettes = signal<AppearancePalette[]>([standardPalette('light'), standardPalette('dark')]);
  readonly choice = signal('system');
  readonly loading = signal(false);
  readonly loadFailed = signal(false);

  initialize(): void {
    if (!this.browser || this.started) return;
    this.started = true;
    this.system = window.matchMedia('(prefers-color-scheme: dark)');
    this.owner = this.sessionOwner(this.auth.session);
    const initialChoice = this.owner ? this.storage.read(CHOICE_PREFIX + this.owner) ?? 'system' : this.publicChoice();
    this.choice.set(initialChoice);
    this.palettes.set(this.initialPalettes(this.owner, initialChoice));
    this.apply();
    const update = () => this.apply();
    this.system.addEventListener('change', update);
    this.destroyRef.onDestroy(() => this.system?.removeEventListener('change', update));
    this.router.events.pipe(filter(event => event instanceof NavigationEnd), takeUntilDestroyed(this.destroyRef)).subscribe(() => {
      this.routerReady = true;
      update();
      if (!this.loading()) void this.reload();
    });
    const resume = () => { if (this.document.visibilityState === 'visible') void this.reload(); };
    this.document.addEventListener('visibilitychange', resume);
    this.destroyRef.onDestroy(() => this.document.removeEventListener('visibilitychange', resume));
    this.auth.session$.pipe(map(session => this.sessionOwner(session)), distinctUntilChanged(), skip(1),
      takeUntilDestroyed(this.destroyRef)).subscribe(owner => {
      this.owner = owner;
      this.accountGeneration++;
      this.deleted.clear();
      this.draft = null;
      this.generation++;
      const choice = owner ? this.storage.read(CHOICE_PREFIX + owner) ?? 'system' : this.publicChoice();
      this.choice.set(choice);
      this.palettes.set(this.initialPalettes(owner, choice));
      this.apply();
      void this.reload();
    });
    void this.reload();
  }

  async reload(): Promise<void> {
    const owner = this.owner;
    const token = this.auth.session?.accessToken;
    if (owner && !token) return;
    const generation = ++this.generation;
    this.loading.set(true);
    this.loadFailed.set(false);
    try {
      const catalog = await firstValueFrom(this.cache.get({id: `appearance.${owner || 'public'}`, url: owner ? this.url : `${this.url}/standard`,
        schema: APPEARANCE_SCHEMA, isData: validCatalog, headers: owner ? {Authorization: `Bearer ${token}`} : undefined}, 'en'));
      if (generation !== this.generation) return;
      const previous = this.selectedChoicePalette() ?? this.selectedSnapshot(owner, this.choice());
      this.palettes.set(catalogPalettes(catalog.palettes));
      this.storage.write(STANDARDS_KEY, JSON.stringify({schemaVersion: APPEARANCE_SCHEMA, palettes: catalog.palettes.filter(palette => palette.isStandard)}));
      this.reconcileChoice(owner, previous);
      this.apply();
    } catch {
      if (generation === this.generation) this.loadFailed.set(true);
    } finally {
      if (generation === this.generation) this.loading.set(false);
    }
  }

  private reconcileChoice(owner: string, previous: AppearancePalette | null): void {
    if (!owner || this.choice() === 'system') return;
    const legacy = this.choice();
    if (legacy === 'light' || legacy === 'dark') {
      this.selectStandard(legacy);
    }
    if (!this.selectedChoicePalette()) {
      this.storage.write(SELECTED_PREFIX + owner, 'null');
      if (previous) this.selectStandard(previous.baseScheme);
    }
  }

  async save(palette: AppearancePalette): Promise<AppearancePalette> {
    const owner = this.owner;
    const accountGeneration = this.accountGeneration;
    const token = this.auth.session?.accessToken;
    if (!owner || !token || palette.standard) throw new Error('Palette cannot be saved.');
    const model: SaveAppearancePaletteDto = {name: palette.name, baseScheme: palette.baseScheme, colors: {...palette.colors}};
    const options = {headers: {Authorization: `Bearer ${token}`}};
    const request = palette.id ? this.http.put<ApiResponse<AppearancePaletteDto>>(`${this.url}/${palette.id}`, model, options)
      : this.http.post<ApiResponse<AppearancePaletteDto>>(this.url, model, options);
    const response = await firstValueFrom(request);
    if (!validPalette(response.data) || owner !== this.owner || accountGeneration !== this.accountGeneration
      || this.deleted.has(response.data.id)) throw new Error('Invalid palette response.');
    const saved = {...response.data, colors: normalizeColors(response.data.baseScheme, response.data.colors),
      standard: response.data.isStandard};
    this.generation++;
    this.loading.set(false);
    this.palettes.update(palettes => [...palettes.filter(item => item.id !== saved.id), saved]);
    this.apply();
    return saved;
  }

  async delete(palette: AppearancePalette): Promise<void> {
    const owner = this.owner;
    const accountGeneration = this.accountGeneration;
    const token = this.auth.session?.accessToken;
    const key = `${owner}/${palette.id}`;
    if (!owner || !token || palette.standard || !palette.id || this.deleting.has(key)) {
      throw new Error('Palette cannot be deleted.');
    }
    this.deleting.add(key);
    try {
      await firstValueFrom(this.http.delete(`${this.url}/${palette.id}`, {headers: {Authorization: `Bearer ${token}`}}));
      this.cache.invalidate({id: `appearance.${owner}`, url: this.url, schema: APPEARANCE_SCHEMA, isData: validCatalog}, 'en');
      if (owner !== this.owner || accountGeneration !== this.accountGeneration) throw new Error('Account changed.');
      this.generation++;
      this.loading.set(false);
      this.deleted.add(palette.id);
      this.palettes.update(palettes => palettes.filter(item => item.id !== palette.id));
      if (this.choice() === palette.id) this.selectStandard(palette.baseScheme);
      this.apply();
    } finally {
      this.deleting.delete(key);
    }
  }

  select(id: string): void {
    if (id !== 'system' && !this.palettes().some(palette => palette.id === id)) return;
    const palette = this.palettes().find(item => item.id === id);
    const choice = !this.owner && palette?.standard ? palette.baseScheme : id;
    this.choice.set(choice);
    this.storage.write(this.owner ? CHOICE_PREFIX + this.owner : PUBLIC_APPEARANCE_KEY, choice);
    this.apply();
  }

  selectStandard(baseScheme: 'light' | 'dark'): void {
    const palette = this.palettes().find(item => item.standard && item.baseScheme === baseScheme);
    if (palette) this.select(palette.id);
  }

  preview(palette: AppearancePalette): void {
    this.draft = structuredClone(palette);
    this.apply();
  }

  endPreview(): void {
    this.draft = null;
    this.apply();
  }

  activePalette(): AppearancePalette {
    const base = this.system?.matches ? 'dark' : 'light';
    const system = this.palettes().find(palette => palette.standard && palette.baseScheme === base) ?? standardPalette(base);
    return this.choice() === 'system' ? system : this.palettes().find(palette => palette.id === this.choice()
      || (palette.standard && palette.baseScheme === this.choice())) ?? system;
  }

  private apply(): void {
    if (!this.browser) return;
    const currentUrl = this.currentUrl();
    const path = stripLocalizedRouteLanguageFromPathname(currentUrl.split(/[?#]/)[0]);
    const login = path === '/login';
    const publicPage = isServerRenderedRoutePathname(currentUrl.split(/[?#]/)[0]);
    const internal = !!this.owner && (path === '/about' || /^\/(home|inventory|basket|qr-labels|write-off|relocation|reports|people|feedback|moderation|administration|profile)(\/|$)/.test(path));
    const root = this.document.documentElement;
    if (Capacitor.isNativePlatform()) root.style.removeProperty('background-color');
    this.appliedKeys.forEach(key => root.style.removeProperty(key));
    this.appliedKeys = [];
    root.classList.toggle('appearance-enabled', login || publicPage || internal);
    if (!login && !publicPage && !internal) {
      delete root.dataset['appearanceChoice'];
      delete root.dataset['appearanceBase'];
      root.style.removeProperty('color-scheme');
      this.nativeBackground = '#edf2f8';
      this.nativeNavigationBackground = '#edf2f8';
      this.nativeWebViewBackground = '#edf2f8';
      void this.refreshNativeChrome();
      return;
    }
    const loginAppearance = login && !Capacitor.isNativePlatform() ? this.loginAppearance(currentUrl) : null;
    const palette = loginAppearance?.palette ?? (login && !this.owner
      ? this.lastPalette() ?? this.activePalette()
      : this.draft ?? this.activePalette());
    const styles = paletteStyles(palette.colors);
    Object.entries(styles).forEach(([key, value]) => root.style.setProperty(key, value));
    this.appliedKeys = Object.keys(styles);
    root.dataset['appearanceChoice'] = loginAppearance?.choice ?? this.choice();
    root.dataset['appearanceBase'] = palette.baseScheme;
    root.style.colorScheme = palette.baseScheme;
    this.nativeBackground = opaqueStatusBarColor(palette.colors['header.normal.background'] ?? '#edf2f8',
      palette.colors['surface.normal.background'] ?? '#ffffff');
    this.nativeNavigationBackground = opaqueStatusBarColor(palette.colors['footer.normal.background'] ?? '#edf2f8',
      palette.colors['canvas.normal.backgroundEnd'] ?? '#ffffff');
    this.nativeWebViewBackground = opaqueStatusBarColor(palette.colors['canvas.normal.background'] ?? '#edf2f8',
      palette.colors['surface.normal.background'] ?? '#ffffff');
    if (Capacitor.isNativePlatform()) root.style.backgroundColor = this.nativeWebViewBackground;
    void this.refreshNativeChrome();
    this.persistSelectedPalette(palette, internal || publicPage);
  }

  private sessionOwner(session: AuthSession | null): string {
    return isAuthSessionActive(session) && typeof session.user?.id === 'string' ? session.user.id : '';
  }

  private persistSelectedPalette(palette: AppearancePalette, accountRoute: boolean): void {
    if (!this.owner || !accountRoute || this.draft) return;
    if (!this.selectedChoicePalette()) return;
    if (this.choice() !== 'system') {
      this.storage.write(SELECTED_PREFIX + this.owner,
        JSON.stringify({owner: this.owner, choice: this.choice(), palette}));
    }
    this.storage.write(LAST_PALETTE_KEY, JSON.stringify(palette));
    this.storage.write(LAST_CHOICE_KEY, this.choice());
  }

  private currentUrl(): string {
    const location = this.document.location;
    return !this.routerReady && !this.router.navigated && location?.pathname
      ? `${location.pathname}${location.search}${location.hash}`
      : this.router.url;
  }

  private loginAppearance(currentUrl: string): {palette: AppearancePalette; choice: string} {
    const source = new URLSearchParams(currentUrl.split('?')[1]?.split('#')[0] ?? '').get('appearanceSource');
    if (source === 'landing') {
      const choice = this.publicChoice();
      return {palette: this.standardForChoice(choice), choice};
    }
    if (source === 'logout') {
      const choice = this.storage.read(LAST_CHOICE_KEY);
      if (choice === 'system') return {palette: this.standardForChoice('system'), choice};
    }
    const lastPalette = this.lastPalette();
    return lastPalette
      ? {palette: lastPalette, choice: lastPalette.id}
      : {palette: this.standardForChoice('system'), choice: 'system'};
  }

  private standardForChoice(choice: 'light' | 'dark' | 'system'): AppearancePalette {
    const base = choice === 'system' ? (this.system?.matches ? 'dark' : 'light') : choice;
    return this.palettes().find(palette => palette.standard && palette.baseScheme === base) ?? standardPalette(base);
  }

  private selectedChoicePalette(): AppearancePalette | null {
    const choice = this.choice();
    return choice === 'system' ? this.activePalette() : this.palettes().find(palette => palette.id === choice
      || (palette.standard && palette.baseScheme === choice)) ?? null;
  }

  private initialPalettes(owner: string, choice: string): AppearancePalette[] {
    const standards = this.initialStandards();
    if (!owner || choice === 'system' || standards.some(palette => palette.id === choice)) return standards;
    const selected = this.selectedSnapshot(owner, choice);
    return selected ? [...standards, selected] : standards;
  }

  private selectedSnapshot(owner: string, choice: string): AppearancePalette | null {
    try {
      const snapshot: unknown = JSON.parse(this.storage.read(SELECTED_PREFIX + owner) ?? 'null');
      if (!snapshot || typeof snapshot !== 'object') return null;
      const record = snapshot as {owner?: unknown; choice?: unknown; palette?: unknown};
      if (record.owner !== owner || record.choice !== choice || !validPalette(record.palette)
        || record.palette.id !== choice) return null;
      return {...record.palette, colors: normalizeColors(record.palette.baseScheme, record.palette.colors),
        standard: record.palette.isStandard};
    } catch { return null; }
  }

  refreshNativeChrome(): Promise<void> {
    if (!this.browser || !Capacitor.isNativePlatform()) return Promise.resolve();
    // Serialize updates so a delayed native call cannot overwrite a newer preview.
    this.nativeChromeUpdate = this.nativeChromeUpdate.then(async () => {
      await configureMobileSystemBars(this.nativeBackground, this.nativeNavigationBackground, this.nativeWebViewBackground);
    })
      .catch(() => undefined);
    return this.nativeChromeUpdate;
  }

  private lastPalette(): AppearancePalette | null {
    try {
      const palette: unknown = JSON.parse(this.storage.read(LAST_PALETTE_KEY) ?? 'null');
      if (!validPalette(palette)) return null;
      return palette.isStandard
        ? this.palettes().find(item => item.standard && item.baseScheme === palette.baseScheme) ?? null
        : {...palette, colors: normalizeColors(palette.baseScheme, palette.colors), standard: false};
    } catch { return null; }
  }

  private publicChoice(): 'light' | 'dark' | 'system' {
    const choice = this.storage.read(PUBLIC_APPEARANCE_KEY);
    return choice === 'light' || choice === 'dark' || choice === 'system' ? choice : 'system';
  }

  private initialStandards(): AppearancePalette[] {
    try {
      const embedded = this.document.documentElement.dataset['standardPalettes'];
      const catalog: unknown = JSON.parse(embedded || this.storage.read(STANDARDS_KEY) || 'null');
      if (validCatalog(catalog)) {
        delete this.document.documentElement.dataset['standardPalettes'];
        const palettes = catalog.palettes.filter(palette => palette.isStandard);
        this.storage.write(STANDARDS_KEY, JSON.stringify({schemaVersion: APPEARANCE_SCHEMA, palettes}));
        return catalogPalettes(palettes);
      }
    } catch { /* Use bundled standards until the network becomes available. */ }
    return [standardPalette('light'), standardPalette('dark')];
  }
}
